import React, { useCallback, useMemo } from 'react';
import { Button } from 'antd';
import { getUserDetails } from '../../../../utils/helper';
import type { ReportStatus } from '../../types';

interface StatusActionsProps {
  statusList: ReportStatus[];
  statusLoading: boolean;
  /** Effective order state derived from committed reports: SCANNED | DRAFTED | REVIEWED | SIGNEDOFF */
  orderReportState: string;
  /** Status code of the action currently in flight — shows a spinner on that button
   *  and locks the others until it settles */
  pendingAction?: string | null;
  proxyMode?: 'draft' | 'signoff' | null;
  onAction: (statusCode: string) => void;
}

const SIGNOFF_ICON = (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.199951 2.2001C0.199951 1.3176 0.917451 0.600098 1.79995 0.600098H8.19995C9.08245 0.600098 9.79995 1.3176 9.79995 2.2001V12.6276C9.79995 13.2676 9.08745 13.6476 8.55495 13.2926L4.99995 10.9201L1.44495 13.2926C0.912451 13.6476 0.199951 13.2651 0.199951 12.6276V2.2001ZM1.79995 1.8001C1.57995 1.8001 1.39995 1.9801 1.39995 2.2001V11.8801L4.33495 9.9251C4.73745 9.6576 5.26245 9.6576 5.66495 9.9251L8.59995 11.8801V2.2001C8.59995 1.9801 8.41995 1.8001 8.19995 1.8001H1.79995Z" fill="white"/>
  </svg>
);

// Non-final statuses. Once signed off they stay visible but disabled — only Sign off remains
// actionable (re-reporting always produces a new signed entry).
const DRAFT_LIKE = ['DRAFTED', 'REVIEWED'];

const getButtonProps = (statusCode: string) => {
  switch (statusCode) {
    case 'SIGNEDOFF':
      return { type: 'primary' as const, className: 'footer-btn-signoff' };
    default:
      return { type: 'default' as const, className: 'footer-btn' };
  }
};

const StatusActions: React.FC<StatusActionsProps> = ({
  statusList,
  statusLoading,
  orderReportState,
  pendingAction = null,
  proxyMode = null,
  onAction,
}) => {
  const userType = getUserDetails()?.user_type;

  // The workflow only moves forward: SCANNED → DRAFTED → REVIEWED → SIGNEDOFF.
  // Draft locks once the order is REVIEWED; both lock once SIGNEDOFF.
  const getDisabledState = useCallback((statusCode: string) => {
    switch (statusCode) {
      case 'DRAFTED':
        return ['REVIEWED', 'SIGNEDOFF'].includes(orderReportState);
      case 'REVIEWED':
        return orderReportState === 'SIGNEDOFF';
      case 'SIGNEDOFF':
        return !['hod', 'radiologist'].includes(userType);
      default:
        return false;
    }
  }, [orderReportState, userType]);

  const buttons = useMemo(() => {
    const fallback: ReportStatus[] = [
      { status_id: 1, status_code: 'DRAFTED', status_label: 'Draft', status_order: 1 },
      { status_id: 2, status_code: 'REVIEWED', status_label: 'Reviewed', status_order: 2 },
      { status_id: 3, status_code: 'SIGNEDOFF', status_label: 'Sign Off', status_order: 3 },
    ];

    const list = statusLoading || !statusList.length ? fallback : statusList;
    const filtered = list
      .filter(s => !(proxyMode === 'signoff' && DRAFT_LIKE.includes(s.status_code)))
      .filter(s => !(proxyMode === 'draft' && s.status_code === 'SIGNEDOFF'));

    return filtered.map(status => {
      const props = getButtonProps(status.status_code);
      const isPending = pendingAction === status.status_code;
      return (
        <Button
          key={status.status_id}
          className={props.className}
          type={props.type}
          loading={isPending}
          disabled={getDisabledState(status.status_code) || (!!pendingAction && !isPending)}
          onClick={() => onAction(status.status_code)}
        >
          {status.status_code === 'SIGNEDOFF' && !isPending && SIGNOFF_ICON}
          {status.status_label}
        </Button>
      );
    });
  }, [statusList, statusLoading, proxyMode, pendingAction, getDisabledState, onAction]);

  return <>{buttons}</>;
};

export default StatusActions;
